import React from 'react';
import type { PhaseVisualContent } from '../../types';

interface PhaseEducationalCardsProps {
    content: PhaseVisualContent | null | undefined;
    t: (key: string) => string;
    className?: string;
    /** Soft phase tint for What's Happening card */
    phaseAccent?: string;
}

/**
 * Renders My Journey educational blocks from phase content (show flags + translation keys).
 * Missing translations show the key (no English placeholders).
 */
const PhaseEducationalCards: React.FC<PhaseEducationalCardsProps> = ({
    content,
    t,
    className = '',
    phaseAccent,
}) => {
    if (!content) return null;

    const tr = (key: string | undefined) => (key ? t(key) : '');

    const visibleHighlights = (content.highlights ?? []).filter((h) => h.isVisible && h.textKey);
    const showWhats =
        content.showWhatsHappening && !!(content.whatsHappeningBodyKey || content.whatsHappeningTitleKey);
    const showHighlights = content.showDevelopmentHighlights && visibleHighlights.length > 0;
    const showTip = content.showTip && !!content.tipTextKey;

    if (!showWhats && !showHighlights && !showTip) return null;

    const strong = phaseAccent || content.accentColor || 'var(--phase-accent, var(--accent-color))';
    // Soft surface like Figma bgColor (low opacity of strong accent)
    const soft =
        typeof strong === 'string' && (strong.startsWith('#') || strong.startsWith('rgb') || strong.startsWith('var('))
            ? `color-mix(in srgb, ${strong} 16%, white)`
            : 'var(--phase-accent-soft, var(--bg-color))';

    return (
        <div className={`space-y-4 ${className}`.trim()}>
            {showWhats && (
                <div
                    className="rounded-2xl border p-4"
                    style={{
                        backgroundColor: soft,
                        borderColor: `color-mix(in srgb, ${strong} 25%, transparent)`,
                    }}
                >
                    <div className="mb-1.5 flex items-center gap-2">
                        <span className="text-xl" aria-hidden>
                            {content.whatsHappeningEmoji ?? '💬'}
                        </span>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-color)]">
                            {tr(content.whatsHappeningTitleKey) || tr('myrebaby.section.whatsHappening')}
                        </p>
                    </div>
                    <p className="text-sm leading-relaxed text-[var(--text-color)]">
                        {tr(content.whatsHappeningBodyKey)}
                    </p>
                </div>
            )}

            {showHighlights && (
                <div className="rounded-2xl border border-[color-mix(in_srgb,var(--text-color),transparent_88%)] bg-[color-mix(in_srgb,var(--bg-color),white_50%)] p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-color)]/60">
                        {tr('myrebaby.section.developmentHighlights')}
                    </p>
                    <ul className="space-y-2 text-sm text-[var(--text-color)]">
                        {visibleHighlights
                            .slice()
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((h) => (
                                <li key={h.textKey} className="flex items-start gap-2">
                                    <span aria-hidden>{h.emoji ?? '•'}</span>
                                    <span>{tr(h.textKey)}</span>
                                </li>
                            ))}
                    </ul>
                </div>
            )}

            {showTip && (
                <div className="rounded-2xl border border-[color-mix(in_srgb,var(--text-color),transparent_88%)] bg-[color-mix(in_srgb,var(--bg-color),white_30%)] p-3.5">
                    <p className="text-xs leading-relaxed text-[var(--text-color)]">
                        <span className="mr-1" aria-hidden>
                            {content.tipEmoji ?? '💡'}
                        </span>
                        {tr(content.tipTextKey)}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PhaseEducationalCards;
